import type { ApplicationStatusResponse } from "@credit-acceptance/step6-bff-client";

import type { WorkbenchSectionId } from "../types";
import { descriptionsForCodes } from "./dealStatusCatalog";
import { isPreApprovalApp } from "./isPreApprovalApp";

const BEFORE_SUBMIT_BLOCKED_STATUSES = descriptionsForCodes("AP", "PD", "PS");
const PRE_APPROVAL_ELIGIBLE_PHASES = new Set(["Pre-approval Requested", "Pre-approval Pending"]);
const EDITABLE_STATUSES = descriptionsForCodes("CP", "AH");
const AP_STATUSES = descriptionsForCodes("AP");
const PD_OR_PS_STATUSES = descriptionsForCodes("PD", "PS");
const PD_STATUSES = descriptionsForCodes("PD");

/**
 * matrix §4.1 `isBeforeSubmitStatus(status)` — true unless status ∈ {AP, PD,
 * PS}. Gates whether Deal Status/Financial/Vehicle are visible at all
 * (combined with `isPreApprovalApp` via OR at the call site — see
 * {@link isSectionVisibleByDealState}). Fails safe (false) when the status is
 * missing/unrecognized.
 * @param {string | undefined} statusDescription - The deal's `statusDescription`.
 * @returns {boolean} Whether the status is "before submit" (not AP/PD/PS).
 */
export function isBeforeSubmitStatus(statusDescription: string | undefined): boolean {
  if (!statusDescription) {
    return false;
  }
  return !BEFORE_SUBMIT_BLOCKED_STATUSES.has(statusDescription);
}

/**
 * Whether a section gated by {@link isBeforeSubmitStatus} (Deal Status,
 * Financial, Vehicle — matrix §5.1–§5.3) is visible for the current deal
 * state, independent of role. `isBeforeSubmitStatus` OR `isPreApprovalApp`,
 * per matrix §4.1's note that the pre-approval flow bypasses the block.
 * @param {ApplicationStatusResponse | undefined} status - The deal's status/phase.
 * @returns {boolean} Whether the section should render given deal state alone.
 */
export function isSectionVisibleByDealState(status: ApplicationStatusResponse | undefined): boolean {
  return isBeforeSubmitStatus(status?.statusDescription) || isPreApprovalApp(status);
}

/**
 * matrix §4.2 `isStep6DisableInPreApproval(status, phase)` — when true, the
 * whole Financial/Vehicle screen build is skipped (effectively blank/frozen).
 * Ported literally from the matrix's pseudocode:
 * ```
 * IF pre-approval state:
 *     disable if status == AP
 *            OR (status ∈ {PD, PS} AND phase NOT IN {Pre-approval Requested, Pre-approval Pending})
 * ELSE (not pre-approval):
 *     disable if status ∈ {AP, PD, PS}
 * ```
 * "Pre-approval state" is `isPreApprovalApp(status)` — which itself already
 * requires status ∈ {AP, PD} AND phase ∈ {Requested, Pending}, so `PS` can
 * never satisfy the pre-approval branch and always falls to the `ELSE`
 * (matching the matrix's explicit "status == PS" only appearing there).
 * Fails safe (disabled/true) when status is missing/unrecognized.
 * @param {ApplicationStatusResponse | undefined} status - The deal's status/phase.
 * @returns {boolean} Whether the screen should be treated as disabled/frozen.
 */
export function isScreenDisabledByDealState(status: ApplicationStatusResponse | undefined): boolean {
  const statusDescription = status?.statusDescription;
  const phaseDescription = status?.phaseDescription;

  if (!statusDescription) {
    return true;
  }

  if (isPreApprovalApp(status)) {
    if (AP_STATUSES.has(statusDescription)) {
      return true;
    }
    return PD_OR_PS_STATUSES.has(statusDescription) && !PRE_APPROVAL_ELIGIBLE_PHASES.has(phaseDescription ?? "");
  }

  return BEFORE_SUBMIT_BLOCKED_STATUSES.has(statusDescription);
}

/**
 * matrix §4.3 `isStep6ApplicantInfoDisable` / §4.4 `isStep6Save` — the
 * deal-state portion only (role `_R`/`_W` markers and the VLA lock are each
 * handled by their own layer and combined separately — see
 * `resolveSectionPermission`). RW only when:
 * ```
 * status ∈ {CP, AH}
 * OR (isPreApprovalApp AND status == PD AND phase ∈ {Pre-approval Requested, Pre-approval Pending})
 * ```
 * Fails safe (false/not editable) when status is missing/unrecognized.
 * Matrix §4.4's `isStep6Save` differs from §4.3 only in also excluding
 * `INTERNAL_R`/other role markers — same deal-state predicate underneath —
 * so this single function backs both.
 * @param {ApplicationStatusResponse | undefined} status - The deal's status/phase.
 * @returns {boolean} Whether the deal state alone allows editing/saving.
 */
export function isDealStateEditable(status: ApplicationStatusResponse | undefined): boolean {
  const statusDescription = status?.statusDescription;
  const phaseDescription = status?.phaseDescription;

  if (!statusDescription) {
    return false;
  }

  if (EDITABLE_STATUSES.has(statusDescription)) {
    return true;
  }

  return (
    isPreApprovalApp(status) &&
    PD_STATUSES.has(statusDescription) &&
    PRE_APPROVAL_ELIGIBLE_PHASES.has(phaseDescription ?? "")
  );
}

/** Deal-state-only visible/editable verdict for one section, before role or VLA lock are applied. */
export interface DealStateSectionVerdict {
  visible: boolean;
  editable: boolean;
}

/**
 * Picks the correct deal-state primitive(s) per section, since the matrix does
 * NOT gate every section the same way (§5.1–§5.5):
 *
 * - **dealStatus**: visibility = §4.1 `isSectionVisibleByDealState`; the section's
 *   `STATUS_W` role marker is independent of deal state, so editability
 *   here is always `true` (role/lock decide it downstream).
 * - **financial / vehicle**: visibility = §4.2 `!isScreenDisabledByDealState`
 *   (whole-screen build gate); editability = §4.3 `isDealStateEditable`.
 *   These are deliberately different predicates — e.g. under "Contract
 *   Completed" the screen still builds (visible) but is not editable.
 * - **driver / secondSigner**: visibility is role-only per the matrix (no
 *   deal-state section-hiding rule), but editability still shares the §4.3 gate.
 * - **internal**: no whole-section deal-state rule in the matrix at this
 *   granularity (only an Advance-subsection/UW-level check) — left fully
 *   role/lock-gated for now.
 * @param {WorkbenchSectionId} sectionId - Which Step6 section to evaluate.
 * @param {ApplicationStatusResponse | undefined} status - The deal's status/phase.
 * @returns {DealStateSectionVerdict} The deal-state-only visible/editable verdict for that section.
 */
export function getDealStateVerdictForSection(
  sectionId: WorkbenchSectionId,
  status: ApplicationStatusResponse | undefined
): DealStateSectionVerdict {
  switch (sectionId) {
    case "dealStatus":
      return { visible: isSectionVisibleByDealState(status), editable: true };
    case "financial":
    case "vehicle":
      return { visible: !isScreenDisabledByDealState(status), editable: isDealStateEditable(status) };
    case "driver":
    case "secondSigner":
      return { visible: true, editable: isDealStateEditable(status) };
    case "internal":
      return { visible: true, editable: true };
    default: {
      // Exhaustiveness guard: if `SECTION_IDS` (types.ts) grows a 7th section
      // without a case being added above, `sectionId` here is no longer
      // assignable to `never` and this line fails to compile.
      const unhandled: never = sectionId;
      throw new Error(`Unhandled Workbench section id: ${unhandled}`);
    }
  }
}
